import {
  Box,
  Chip as MUIChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import { selectEmail, selectUserId } from '@/entities/session';
import { formatDateFull } from '@/shared/lib';
import { useAppSelector } from '@/shared/model';
import { getViewsRestrictionsCount } from '../../libs/getViewsRestrictionsCount';
import type { User } from '../../model/types';
import { UserActionsMenu } from '../UserActionsMenu/UserActionsMenu';
import { ROLES_LABELS } from './constants';

const Chip = styled(MUIChip)(({ theme }) => ({
  height: 24,
  fontSize: '0.75rem',
  lineHeight: '0.875rem',
  fontWeight: 500,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,

  '& .MuiChip-label': {
    padding: 0,
  },
}));

interface Props {
  users?: User[];
  onUserClick: (user: User) => void;
}

export function UsersTable({ users, onUserClick }: Props) {
  const userId = useAppSelector(selectUserId);
  const userEmail = useAppSelector(selectEmail);
  const sortedUsers = useMemo(() => {
    return users?.toSorted((a, b) => {
      if (a.email === userEmail) {
        return -1;
      }

      if (b.email === userEmail) {
        return 1;
      }

      return a.email.localeCompare(b.email);
    });
  }, [users, userEmail]);

  const handleUserClick = (user: User) => () => {
    onUserClick(user);
  };

  if (!sortedUsers) {
    return null;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          overflow: 'auto',
          borderRadius: 2,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  borderTop: '1px solid #DDDFE7',
                  borderRight: '1px solid #DDDFE7',

                  '&:first-of-type': {
                    borderTopLeftRadius: 8,
                    borderLeft: '1px solid #DDDFE7',
                  },

                  '&:last-of-type': {
                    borderTopRightRadius: 8,
                  },
                },
              }}
            >
              <TableCell sx={{ backgroundColor: '#F1F3F8' }}>Email</TableCell>
              <TableCell sx={{ backgroundColor: '#F1F3F8' }}>Role</TableCell>
              <TableCell
                sx={{ backgroundColor: '#F1F3F8', textAlign: 'right' }}
              >
                Dimensions Filters
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#F1F3F8', textAlign: 'right' }}
              >
                Metrics Filters
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#F1F3F8', textAlign: 'right' }}
              >
                Views Restrictions
              </TableCell>
              <TableCell sx={{ backgroundColor: '#F1F3F8' }}>Status</TableCell>
              <TableCell sx={{ backgroundColor: '#F1F3F8' }}>
                Last Login Time
              </TableCell>
              <TableCell sx={{ width: 52, backgroundColor: '#F1F3F8' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{
                    textAlign: 'center',
                    borderRight: '1px solid #DDDFE7',
                    borderLeft: '1px solid #DDDFE7',
                    borderEndEndRadius: 8,
                    borderEndStartRadius: 8,
                  }}
                >
                  No users found
                </TableCell>
              </TableRow>
            )}
            {sortedUsers?.map((user) => {
              const viewsRestrictionsCount = getViewsRestrictionsCount(
                user.viewsRestrictions,
              );

              return (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={user.id}
                  sx={{
                    '&:last-of-type td': {
                      '&:first-of-type': {
                        borderBottomLeftRadius: 8,
                      },

                      '&:last-of-type': {
                        borderBottomRightRadius: 8,
                      },
                    },

                    '& td': {
                      borderRight: '1px solid #DDDFE7',

                      '&:first-of-type': {
                        borderLeft: '1px solid #DDDFE7',
                      },
                    },
                  }}
                  onClick={handleUserClick(user)}
                >
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{ROLES_LABELS[user.role]}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    {user.rlsFilters.length}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    {user.clsFilters.length}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    {viewsRestrictionsCount}
                  </TableCell>
                  <TableCell>
                    {user.isActive ? (
                      <Chip color="success" label="Active" />
                    ) : (
                      <Chip
                        sx={{ backgroundColor: '#F1F3F8', color: '#646874' }}
                        label="Deactivated"
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      lineHeight: '1.125rem',
                    }}
                  >
                    {user.lastSignInAt
                      ? formatDateFull(user.lastSignInAt)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {userId !== user.id && <UserActionsMenu user={user} />}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
